/**
 * Route Configuration - Single Source of Truth
 * 
 * This file defines all routes with their metadata including:
 * - Path and component
 * - Menu configuration (label, icon, parent, order)
 * - Permissions and module access
 * - Route matching patterns
 */

import { lazy } from 'react';

/**
 * Route metadata structure:
 * {
 *   path: string - Route path
 *   Component: Component - Lazy loaded component
 *   exact?: boolean - Exact match (default: false for dynamic routes)
 *   menu?: {
 *     key: string - Unique menu key
 *     label: string | function - Menu label (can be dynamic via function)
 *     icon?: ReactNode - Menu icon
 *     parent?: string - Parent menu key (for nested menus)
 *     order?: number - Display order
 *     hide?: boolean - Hide from menu but still accessible via URL
 *   }
 *   permissions?: string[] - Required permissions
 *   modules?: string[] - Required module access
 *   matchPattern?: string | RegExp - Custom pattern for matching (default: uses path)
 * }
 */

export const routeConfig = [
  // Dashboard
  {
    path: '/',
    Component: lazy(() => import('../components/dashboard/Dashboard')),
    exact: true,
    menu: {
      key: 'dashboard',
      label: 'Dashboard',
      icon: 'dashboard',
      order: 1,
    },
    permissions: [],
  },
  {
    path: '/dashboard',
    Component: lazy(() => import('../components/dashboard/Dashboard')),
    exact: true,
    menu: {
      key: 'dashboard',
      label: 'Dashboard',
      icon: 'dashboard',
      order: 1,
    },
    permissions: [],
  },
  {
    path: '/dashboard-analytics',
    Component: lazy(() => import('../components/dashboard/DashboardAnalytics')),
    exact: true,
    menu: { hide: true },
  },
  // intro-business-information removed — superseded by Business profile menu.

  // Calendar
  {
    path: '/calendar',
    Component: lazy(() => import('../components/calendars/components/Calendars')),
    exact: true,
    menu: {
      key: 'calendar',
      label: 'Calendar',
      icon: 'calendar',
      order: 2,
    },
    permissions: ['calendar.view'],
    modules: ['calendar'],
  },

  // Sales Module
  {
    path: '/estimates/list',
    Component: lazy(() => import('../components/estimates/components/Estimates')),
    exact: true,
    menu: {
      key: 'estimates',
      label: (authContext) => authContext?.settings?.estimates_menu_title || 'Estimates',
      parent: 'sales',
      order: 1,
    },
    permissions: ['estimates.view'],
    modules: ['estimates'],
    matchPattern: /^\/estimates(\/|$)/,
  },
  {
    path: '/estimates/estimate-details/:id',
    Component: lazy(() => import('../components/estimates/components/EstimateDetail')),
    exact: false,
    menu: { hide: true },
    matchPattern: /^\/estimates\/estimate-details\/.+$/,
  },
  {
    path: '/estimates/estimate-edit/:id',
    Component: lazy(() => import('../components/estimates/components/EstimateForm')),
    exact: false,
    menu: { hide: true },
    matchPattern: /^\/estimates\/estimate-edit\/.+$/,
  },
  {
    path: '/estimates/new',
    Component: lazy(() => import('../components/estimates/components/EstimateForm')),
    exact: true,
    menu: { hide: true },
  },

  {
    path: '/workspaces/list',
    Component: lazy(() => import('../components/workspaces/WorkspacesList')),
    exact: true,
    menu: {
      key: 'workspaces',
      label: 'Workspaces',
      parent: 'sales',
      order: 3,
    },
    permissions: ['customers.view', 'sales.view', 'estimates.view'],
    modules: ['customers', 'sales', 'estimates'],
    matchPattern: /^\/workspaces(\/|$)/,
  },
  {
    path: '/workspaces/:id',
    Component: lazy(() => import('../components/workspaces/WorkspaceDetail')),
    exact: false,
    menu: { hide: true },
    matchPattern: /^\/workspaces\/.+$/,
  },

  // Invoices Module
  {
    path: '/invoices/list',
    Component: lazy(() => import('../components/invoices/components/Invoices')),
    exact: true,
    menu: {
      key: 'invoices',
      label: (authContext) => authContext?.settings?.invoices_menu_title || 'Invoices',
      parent: 'sales',
      order: 2,
    },
    permissions: ['sales.view'],
    modules: ['sales'],
    matchPattern: /^\/invoices(\/|$)/,
  },
  {
    path: '/invoices/invoice-details/:id',
    Component: lazy(() => import('../components/invoices/components/InvoiceDetails')),
    exact: false,
    menu: { hide: true },
    matchPattern: /^\/invoices\/invoice-details\/.+$/,
  },
  {
    path: '/invoices/invoice-edit/:id',
    Component: lazy(() => import('../components/invoices/components/InvoiceForm')),
    exact: false,
    menu: { hide: true },
    matchPattern: /^\/invoices\/invoice-edit\/.+$/,
  },
  {
    path: '/invoices/new',
    Component: lazy(() => import('../components/invoices/components/InvoiceForm')),
    exact: true,
    menu: { hide: true },
  },

  // Customers Module
  {
    path: '/customers/list',
    Component: lazy(() => import('../components/customers/components/Customers')),
    exact: true,
    menu: {
      key: 'customers',
      label: (authContext) => authContext?.settings?.customers_menu_title || 'Customers',
      parent: 'sales',
      order: 4,
    },
    permissions: ['customers.view'],
    modules: ['customers'],
    matchPattern: /^\/customers(\/|$)/,
  },
  {
    path: '/customers/customer-details/:id',
    Component: lazy(() => import('../components/customers/components/CustomerDetails')),
    exact: false,
    menu: { hide: true },
    matchPattern: /^\/customers\/customer-details\/.+$/,
  },
  {
    path: '/customers/customer-edit/:id',
    Component: lazy(() => import('../components/customers/components/CustomerForm')),
    exact: false,
    menu: { hide: true },
    matchPattern: /^\/customers\/customer-edit\/.+$/,
  },
  // {
  //   path: '/customers/new',
  //   Component: lazy(() => import('../components/customers/components/CustomerDetails')),
  //   exact: false,
  //   menu: { hide: true },
  // },
  {
    path: '/customers-statements',
    Component: lazy(() => import('../components/customers-statements/components/CustomersStatementsDetails')),
    exact: false,
    menu: {
      key: 'customersstatements',
      label: 'Customers Statements',
      parent: 'accounting',
      order: 2,
    },
    permissions: ['statements.view'],
    modules: ['statements'],
  },

  // Services & Products Module
  {
    path: '/items/list',
    Component: lazy(() => import('../components/services/components/Services')),
    exact: true,
    menu: {
      key: 'services',
      label: (authContext) => {
        const format = authContext?.settings?.format_items || 'item';
        return `${format.charAt(0).toUpperCase() + format.slice(1)}s`;
      },
      parent: 'servicesproducts',
      order: 1,
    },
    permissions: ['items.view'],
    modules: ['items'],
    matchPattern: /^\/items(\/|$)/,
  },
  {
    path: '/items/item-details/:id',
    Component: lazy(() => import('../components/services/components/ServiceDetails')),
    exact: false,
    menu: { hide: true },
    matchPattern: /^\/items\/item-details\/.+$/,
  },
  {
    path: '/items/new',
    Component: lazy(() => import('../components/services/components/ServiceNew')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/services/service-edit/:id',
    Component: lazy(() => import('../components/services/components/ServiceForm')),
    exact: false,
    menu: { hide: true },
    matchPattern: /^\/services\/service-edit\/.+$/,
  },
  {
    path: '/items/items-category/list',
    Component: lazy(() => import('../components/services-category/components/ServicesCategory')),
    exact: true,
    menu: {
      key: 'servicescategories',
      label: (authContext) => {
        const format = authContext?.settings?.format_items || 'item';
        return `${format.charAt(0).toUpperCase() + format.slice(1)} Categories`;
      },
      parent: 'servicesproducts',
      order: 2,
    },
    permissions: ['categories.view'],
    modules: ['categories'],
  },
  {
    path: '/configurable-templates',
    Component: lazy(() => import('../components/service-configurable-templates')),
    exact: true,
    menu: {
      key: 'configurable-templates',
      label: 'Configurable Templates',
      parent: 'servicesproducts',
      order: 3,
    },
    permissions: ['items.view'],
    features: ['items_advanced'],
  },
  {
    path: '/configurable-groups',
    Component: lazy(() => import('../components/configurable-groups/components/ConfigurableGroupsList')),
    exact: true,
    menu: {
      key: 'configurable-groups',
      label: 'Configurable Groups',
      parent: 'servicesproducts',
      order: 4,
    },
    permissions: ['items.view'],
    features: ['items_advanced'],
  },
  {
    path: '/configurable-groups/new',
    Component: lazy(() => import('../components/configurable-groups/components/ConfigurableGroupDetails')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/configurable-groups/:id',
    Component: lazy(() => import('../components/configurable-groups/components/ConfigurableGroupDetails')),
    exact: false,
    menu: { hide: true },
    matchPattern: /^\/configurable-groups\/.+$/,
  },

  // Inventory Module
  {
    path: '/inventory-items/list',
    Component: lazy(() => import('../components/inventory-items/components/InventoryItems')),
    exact: true,
    menu: {
      key: 'inventory-items',
      label: 'Inventory Items',
      parent: 'inventory',
      order: 1,
    },
    permissions: ['inventory.view'],
    modules: ['inventory'],
    matchPattern: /^\/inventory-items(\/|$)/,
  },
  {
    path: '/inventory-items/item-details/:id',
    Component: lazy(() => import('../components/inventory-items/components/InventoryItemDetails')),
    exact: false,
    menu: { hide: true },
    matchPattern: /^\/inventory-items\/item-details\/.+$/,
  },
  {
    path: '/inventory-items/new',
    Component: lazy(() => import('../components/inventory-items/components/InventoryItemForm')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/inventory/categories',
    Component: lazy(() => import('../components/inventory-categories/components/InventoryCategoriesList')),
    exact: true,
    menu: {
      key: 'inventory-categories',
      label: 'Inventory Categories',
      parent: 'inventory',
      order: 2,
    },
    permissions: ['inventory_categories.view'],
    modules: ['inventory'],
  },
  {
    path: '/inventory/warehouses',
    Component: lazy(() => import('../components/warehouses/components/Warehouses')),
    exact: true,
    menu: {
      key: 'warehouses',
      label: 'Warehouses',
      parent: 'inventory',
      order: 3,
    },
    permissions: ['inventory.view'],
    modules: ['inventory'],
  },
  {
    path: '/inventory/transfers',
    Component: lazy(() => import('../components/warehouse-transfers/components/WarehouseTransfers')),
    exact: true,
    menu: {
      key: 'warehouse-transfers',
      label: 'Stock Transfers',
      parent: 'inventory',
      order: 4,
    },
    permissions: ['inventory.view'],
    modules: ['inventory'],
  },

  // Accounting Module
  {
    path: '/receive-payments',
    Component: lazy(() => import('../components/receive-payments/components/ReceivePayments')),
    exact: true,
    menu: {
      key: 'receivepayments',
      label: 'Receive Payments',
      parent: 'accounting',
      order: 1,
    },
    permissions: ['payments_receivable.view'],
    modules: ['payments_receivable'],
  },
  {
    path: '/receive-payments/add-payment',
    Component: lazy(() => import('../components/receive-payments/components/ReceivePaymentForm')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/pay-bills',
    Component: lazy(() => import('../components/pay-bills/components/PayBills')),
    exact: true,
    menu: {
      key: 'paybills',
      label: 'Pay Bills',
      parent: 'accounting',
      order: 2,
    },
    permissions: ['payments_payable.view'],
    modules: [],
  },
  {
    path: '/pay-bills/add-payment',
    Component: lazy(() => import('../components/pay-bills/components/PayBillForm')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/sales-tax',
    Component: lazy(() => import('../components/sales-tax/components/SalesTaxes')),
    exact: true,
    menu: {
      key: 'salestaxes',
      label: 'Sales Taxes',
      parent: 'accounting',
      order: 3,
    },
    permissions: ['taxes.view'],
    modules: ['taxes'],
    matchPattern: /^\/sales-tax(\/|$)/,
  },
  {
    path: '/sales-tax/sales-tax-details/:id',
    Component: lazy(() => import('../components/sales-tax/components/SalesTaxDetails')),
    exact: false,
    menu: { hide: true },
    matchPattern: /^\/sales-tax\/sales-tax-details\/.+$/,
  },
  {
    path: '/sales-tax/new',
    Component: lazy(() => import('../components/sales-tax/components/SalesTaxDetails')),
    exact: true,
    menu: { hide: true },
  },

  // Purchases Module
  {
    path: '/suppliers/list',
    Component: lazy(() => import('../components/vendors/components/Suppliers')),
    exact: true,
    menu: {
      key: 'suppliers',
      label: 'Suppliers',
      parent: 'purchases',
      order: 1,
    },
    permissions: ['suppliers.view'],
    modules: ['suppliers'],
    matchPattern: /^\/suppliers(\/|$)/,
  },
  {
    path: '/suppliers/supplier-details/:id',
    Component: lazy(() => import('../components/vendors/components/SupplierDetails')),
    exact: false,
    menu: { hide: true },
    matchPattern: /^\/suppliers\/supplier-details\/.+$/,
  },
  {
    path: '/bills/list',
    Component: lazy(() => import('../components/bills/components/Bills')),
    exact: true,
    menu: {
      key: 'bills',
      label: 'Bills',
      parent: 'purchases',
      order: 2,
    },
    permissions: ['bills.view'],
    modules: ['bills'],
    matchPattern: /^\/bills(\/|$)/,
  },
  {
    path: '/bills/bill-details/:id',
    Component: lazy(() => import('../components/bills/components/BillDetails')),
    exact: false,
    menu: { hide: true },
    matchPattern: /^\/bills\/bill-details\/.+$/,
  },
  {
    path: '/bills/bill-edit/:id',
    Component: lazy(() => import('../components/bills/components/BillForm')),
    exact: false,
    menu: { hide: true },
    matchPattern: /^\/bills\/bill-edit\/.+$/,
  },
  {
    path: '/bills/new',
    Component: lazy(() => import('../components/bills/components/BillForm')),
    exact: true,
    menu: { hide: true },
  },

  // Personal Module
  {
    path: '/profile',
    Component: lazy(() => import('../components/myProfile/components/MyProfileDetails')),
    exact: true,
    menu: {
      key: 'myprofile',
      label: 'My Profile',
      icon: 'profile',
      parent: 'personal',
      order: 1,
    },
    permissions: [],
  },
  {
    path: '/my-profile-edit',
    Component: lazy(() => import('../components/myProfile/components/MyProfileForm')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/todos',
    Component: lazy(() => import('../components/todos/components/Todos')),
    exact: true,
    menu: {
      key: 'todos',
      label: 'Todos',
      icon: 'todos',
      parent: 'personal',
      order: 2,
    },
    permissions: ['todos.view'],
    modules: ['todos'],
  },

  // Organization Module
  {
    path: '/users/list',
    Component: lazy(() => import('../components/users/components/Users')),
    exact: true,
    menu: {
      key: 'users',
      label: 'Users',
      parent: 'myOrganization',
      order: 1,
    },
    permissions: ['users.view'],
    modules: ['users'],
    matchPattern: /^\/users(\/|$)/,
  },
  {
    path: '/users/user-edit/:id',
    Component: lazy(() => import('../components/users/components/UserDetails')),
    exact: false,
    menu: { hide: true },
    matchPattern: /^\/users\/user-edit\/.+$/,
  },
  {
    path: '/users/user-details/:id',
    Component: lazy(() => import('../components/users/components/UserDetails')),
    exact: false,
    menu: { hide: true },
    matchPattern: /^\/users\/user-details\/.+$/,
  },
  // Business profile group — three sub-pages (Branding, Business info, Welcome
  // page). The parent appears as an expandable menu row inside the Organization
  // group with its own icon; the URL /business-profile redirects to Branding
  // so the parent row is also clickable.
  {
    path: '/business-profile',
    Component: lazy(() => import('../components/business-profile/BusinessProfileIndex')),
    exact: true,
    menu: { hide: true },
    permissions: ['business_profile.view'],
    modules: ['business_profile'],
  },
  {
    path: '/business-profile/branding',
    Component: lazy(() => import('../components/business-profile/branding/BrandingPage')),
    exact: true,
    menu: {
      key: 'businessprofile_branding',
      label: 'Branding',
      parent: 'businessprofile',
      order: 1,
    },
    permissions: ['business_profile.view'],
    modules: ['business_profile'],
  },
  {
    path: '/business-profile/business-info',
    Component: lazy(() => import('../components/business-profile/business-info/BusinessInfoPage')),
    exact: true,
    menu: {
      key: 'businessprofile_info',
      label: 'Business info',
      parent: 'businessprofile',
      order: 2,
    },
    permissions: ['business_profile.view'],
    modules: ['business_profile'],
  },
  {
    path: '/business-profile/welcome',
    Component: lazy(() => import('../components/business-profile/welcome/WelcomePage')),
    exact: true,
    menu: {
      key: 'businessprofile_welcome',
      label: 'Welcome page',
      parent: 'businessprofile',
      order: 3,
    },
    permissions: ['business_profile.view'],
    modules: ['business_profile'],
  },
  {
    path: '/admin-settings/business-billing',
    Component: lazy(() =>
      import('../components/admin-settings/company/components/CompanyBillingDetails')
    ),
    exact: true,
    menu: {
      key: 'billingbusinessinformation',
      label: 'Billing contact',
      parent: 'myOrganization',
      order: 3,
    },
    permissions: ['organization_billing.view'],
    modules: [],
  },
  {
    path: '/bank-accounts',
    Component: lazy(() => import('../components/admin-settings/bank-accounts/components/PaymentStripe')),
    exact: true,
    menu: {
      key: 'bankaccounts',
      label: 'Bank Accounts',
      parent: 'myOrganization',
      order: 4,
    },
    permissions: ['bank_accounts.view'],
    modules: ['bank_accounts'],
  },

  // System/Admin Module
  {
    path: '/settings',
    Component: lazy(() => import('../components/Settings/components/Settings')),
    exact: true,
    menu: {
      key: 'settings',
      label: 'Settings',
      icon: 'settings',
      order: 1,
    },
    permissions: ['settings.view'],
    modules: ['settings'],
  },
  {
    path: '/admin-settings/configurations',
    Component: lazy(() => import('../components/admin-settings/configurations/components/IncConfigurations')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/licenses',
    Component: lazy(() => import('../components/licenses/components/Licenses')),
    exact: true,
    menu: {
      key: 'licenses',
      label: 'Licenses',
      icon: 'licenses',
      order: 2,
    },
    permissions: ['organization_licenses.view'],
    modules: ['organization_licenses'],
  },
  {
    path: '/licenses/success',
    Component: lazy(() => import('../components/licenses/components/SuccessPage')),
    exact: true,
    menu: { hide: true },
  },

  // Reports
  {
    path: '/reports',
    Component: lazy(() => import('../components/reports/components/ReportsLanding')),
    exact: true,
    menu: {
      key: 'reports',
      label: 'Reports',
      icon: 'reports',
      order: 1,
    },
    permissions: ['reports.view'],
    modules: ['reports'],
    matchPattern: /^\/reports(\/|$)/,
  },
  {
    path: '/reports/inventory',
    Component: lazy(() => import('../components/reports/components/inventory/InventoryReport')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/reports/inventory/transactions',
    Component: lazy(() => import('../components/reports/components/inventory/InventoryTransactionsReport')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/reports/payment-collection',
    Component: lazy(() => import('../components/reports/components/payments/PaymentReport')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/reports/invoices',
    Component: lazy(() => import('../components/reports/components/sales/SalesReport')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/reports/bills',
    Component: lazy(() => import('../components/reports/components/bills/BillReport')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/reports/taxes',
    Component: lazy(() => import('../components/reports/components/taxes/TaxReport')),
    exact: true,
    menu: { hide: true },
    permissions: ['reports.taxes'],
  },
  {
    path: '/reports/emails',
    Component: lazy(() => import('../components/reports/components/emails/EmailReport')),
    exact: true,
    menu: { hide: true },
  },

  // Support
  {
    path: '/support',
    Component: lazy(() => import('../components/support/Support')),
    exact: true,
    menu: {
      key: 'support',
      label: 'Support',
      icon: 'support',
      order: 1,
    },
    permissions: [],
  },

  // Auth routes (hidden from menu)
  {
    path: '/auth/logout',
    Component: lazy(() => import('../components/auth/split/Logout')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/auth/login',
    Component: lazy(() => import('../components/auth/split/Login')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/auth/forget-password',
    Component: lazy(() => import('../components/auth/split/PasswordForget')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/auth/reset-password',
    Component: lazy(() => import('../components/auth/split/PasswordReset')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/auth/register',
    Component: lazy(() => import('../components/auth/split/Registration')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/auth/register/park/pending-creating-database',
    Component: lazy(() => import('../components/auth/split/WaitingForDatabase')),
    exact: true,
    menu: { hide: true },
  },

  // Error pages
  {
    path: '/errors/404',
    Component: lazy(() => import('../components/errors/Error404')),
    exact: true,
    menu: { hide: true },
  },

  // Public routes
  {
    path: '/park/pending-creating-database',
    Component: lazy(() => import('../components/public/park/CreatingDatabase')),
    exact: true,
    menu: { hide: true },
  },
  // SharePoint hub — every route shares the same chrome (CompanyHeader /
  // CompanyInfo / split panel) via SharePoint.js, then renders one section
  // child. The flat route list is required by the top-level router.
  {
    path: 'public/sharepoint/:orgPublicId',
    Component: lazy(() => import('../components/public/sharePoint/pages/SharePointProfilePage')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: 'public/sharepoint/:orgPublicId/sale/:invoicePublicId',
    Component: lazy(() => import('../components/public/sharePoint/SharePoint')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: 'public/sharepoint/:orgPublicId/estimate/:estimatePublicId',
    Component: lazy(() => import('../components/public/sharePoint/SharePoint')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: 'public/sharepoint/:orgPublicId/register',
    Component: lazy(() => import('../components/public/sharePoint/pages/SharePointRegisterPage')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: 'public/sharepoint/:orgPublicId/booking',
    Component: lazy(() => import('../components/public/sharePoint/pages/SharePointBookingPage')),
    exact: true,
    menu: { hide: true },
  },

  // Appatch routes
  {
    path: '/appatch',
    Component: lazy(() => import('../01_site_appatch/components/landing/components/Landing')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/appatch/services/json-formatter',
    Component: lazy(() => import('../01_site_appatch/components/jsonFormatter/components/JsonFormatter')),
    exact: true,
    menu: { hide: true },
  },

  // Development/Design routes (hidden)
  {
    path: '/chatbot',
    Component: lazy(() => import('../components/chatbot/components/Chatbot')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/design',
    Component: lazy(() => import('../components/design/components/Design')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/secondary-sidebar',
    Component: lazy(() => import('../components/common/SecondarySidebar/SecondarySidebarExample')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/dev',
    Component: lazy(() => import('../components/dev/DevIndex')),
    exact: true,
    menu: { hide: true },
  },
  {
    path: '/dev/action-card',
    Component: lazy(() => import('../components/invoices/components/partials/headers/InvoiceActionCardPreview')),
    exact: true,
    menu: { hide: true },
    dev: {
      title: 'Invoice Action Card',
      description: 'Editorial direction — all six lifecycle states (confirmed, overdue, partial, paid, cancelled, completed).',
    },
  },
  {
    path: '/dev/estimate-action-card',
    Component: lazy(() => import('../components/estimates/components/partials/headers/EstimateActionCardPreview')),
    exact: true,
    menu: { hide: true },
    dev: {
      title: 'Estimate Action Card',
      description: 'Three lifecycle states for estimates — awaiting decision, accepted, converted.',
    },
  },
  {
    path: '/dev/bill-action-card',
    Component: lazy(() => import('../components/bills/components/partials/BillActionCardPreview')),
    exact: true,
    menu: { hide: true },
    dev: {
      title: 'Bill Action Card',
      description: 'Vendor bill states — unpaid, partial, overdue, paid in full.',
    },
  },
  {
    path: '/dev/summary-minimal-card',
    Component: lazy(() => import('../components/invoices/components/partials/SummarySection/InvoiceSummaryMinimalCardPreview')),
    exact: true,
    menu: { hide: true },
    dev: {
      title: 'Invoice Summary · Minimal Card',
      description: 'Right-rail lower card — dates, line items, totals, payment history, audit block. All seven lifecycle states.',
    },
  },
  {
    path: '/dev/estimate-summary-minimal-card',
    Component: lazy(() => import('../components/estimates/components/partials/SummarySection/EstimateSummaryMinimalCardPreview')),
    exact: true,
    menu: { hide: true },
    dev: {
      title: 'Estimate Summary · Minimal Card',
      description: 'Estimate right-rail lower card — decision-driven status replaces the amount-due column. Six lifecycle states.',
    },
  },
  {
    path: '/dev/bill-summary-minimal-card',
    Component: lazy(() => import('../components/bills/components/partials/SummarySection/BillSummaryMinimalCardPreview')),
    exact: true,
    menu: { hide: true },
    dev: {
      title: 'Bill Summary · Minimal Card',
      description: 'Vendor bill right-rail lower card — same layout as invoice with inline cancel-payment action. Five lifecycle states.',
    },
  },
  {
    path: '/dev/pulse-dot',
    Component: lazy(() => import('../components/common/PulseDotPreview')),
    exact: true,
    menu: { hide: true },
    dev: {
      title: 'PulseDot · Component Playground',
      description: 'Pulse dot status indicator across buttons, cards, tags, list rows, menus, avatars, and tabs. Six color variants plus animated/static modes.',
    },
  },
];

/**
 * Helper function to get route by path
 */
export const getRouteByPath = (pathname) => {
  return routeConfig.find(route => {
    if (route.matchPattern) {
      return route.matchPattern.test(pathname);
    }
    if (route.exact) {
      return route.path === pathname;
    }
    return pathname.startsWith(route.path);
  });
};

/**
 * Helper function to get all routes for a specific path pattern
 * Useful for finding parent/child relationships
 */
export const getRoutesByPattern = (pattern) => {
  if (typeof pattern === 'string') {
    return routeConfig.filter(route => 
      route.path.startsWith(pattern) || 
      (route.matchPattern && route.matchPattern.source.includes(pattern))
    );
  }
  if (pattern instanceof RegExp) {
    return routeConfig.filter(route => 
      pattern.test(route.path) ||
      (route.matchPattern && pattern.test(route.path))
    );
  }
  return [];
};
